import { useMutation } from "@shared/lib/hooks";
import { BriefService } from "../services/brief.service";
import {
  BRIEF_INFLUENCER_V2_QUERY_KEY,
  BRIEF_LIST_QUERY_KEY,
  makeBriefInfluencerQueryKey,
  makeBriefSingleInfluencerQueryKey,
} from "@entities/brief/lib/constants";
import { ISuggestInfluencerRequest } from "@shared/lib/types/brief";
import { ToastService } from "@shared/lib/services";
import {
  Avatar,
  Collapse,
  Flex,
  List,
  Modal,
  Space,
  Tooltip,
  Typography,
} from "antd";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import { Button } from "@shared/ui/Button";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";
import { CheckSquareTwoTone, InfoCircleOutlined } from "@ant-design/icons";

const { Text } = Typography;

export function useSuggestInfluencer(influencerId: string) {
  const {
    mutate: suggest,
    isLoading: isSuggesting,
    isSuccess: isSuggested,
  } = useMutation({
    mutationFn: ({ data }: { data: ISuggestInfluencerRequest }) =>
      BriefService.suggestInfluencer(data)
        .then((res: any) => {
          if (res.statusCode === 201) {
            const resultItems =
              (res.data as {
                briefInfluencer: IBriefInfluencerV2;
                reason: string;
                status: "failed" | "success";
              }[]) || [];

            const failedItems = resultItems.filter(
              (item) => item.status === "failed"
            );
            const successItems = resultItems.filter(
              (item) => item.status === "success"
            );

            Modal.error({
              icon: <></>,
              title: "Creators already in campaign",
              okButtonProps: {
                type: "primary",
                style: {
                  background: "#33312F",
                },
              },
              content: (
                <Flex vertical gap={8}>
                  <Collapse
                    ghost
                    defaultActiveKey={["failed"]}
                    expandIcon={() => <></>}
                    bordered={false}
                    items={[
                      {
                        key: "failed",
                        styles: {
                          header: {
                            background: "#F8E0DE",
                            borderRadius: 8,
                            borderColor: "#F8E0DE",
                          },
                        },
                        label: (
                          <div>
                            <Text>
                              {" "}
                              <Text strong>{failedItems.length} </Text>
                              can’t be added to campaign.
                            </Text>
                          </div>
                        ),
                        collapsible: "disabled",
                        children: (
                          <List
                            dataSource={failedItems}
                            renderItem={(item) => {
                              const briefInfluencer = item?.briefInfluencer;
                              return (
                                <List.Item>
                                  <List.Item.Meta
                                    title={
                                      <Flex justify="space-between">
                                        <Space align="center">
                                          <Button
                                            style={{ padding: 0 }}
                                            type="link"
                                            onClick={() => {
                                              window.open(
                                                briefInfluencer?.socialMedia
                                                  ?.link,
                                                "_blank"
                                              );
                                            }}
                                          >
                                            {getPlatformIcon(
                                              briefInfluencer?.socialMedia
                                                ?.platform || ""
                                            )}
                                            <Avatar
                                              shape="circle"
                                              size="small"
                                              src={
                                                briefInfluencer?.socialMedia
                                                  ?.profilePicUrl
                                              }
                                            />
                                            <Text strong>
                                              {
                                                briefInfluencer?.socialMedia
                                                  ?.username
                                              }
                                            </Text>
                                          </Button>
                                        </Space>
                                        <Tooltip title={item.reason}>
                                          <InfoCircleOutlined />
                                        </Tooltip>
                                      </Flex>
                                    }
                                  />
                                </List.Item>
                              );
                            }}
                          />
                        ),
                      },
                    ]}
                  />
                  {successItems.length > 0 && (
                    <Collapse
                      defaultActiveKey={["success"]}
                      ghost
                      bordered={false}
                      items={[
                        {
                          key: "success",
                          styles: {
                            header: {
                              background: "#E5F2E8",
                              borderRadius: 8,
                              borderColor: "#E5F2E8",
                            },
                          },
                          label: (
                            <div>
                              <Text>
                                {" "}
                                <Text strong>{successItems.length} </Text>
                                added to campaign{" "}
                                <Text strong style={{ color: "#00875A" }}>
                                  successfully{" "}
                                </Text>
                              </Text>
                            </div>
                          ),
                          children: (
                            <List
                              dataSource={successItems}
                              renderItem={(item) => {
                                const briefInfluencer = item?.briefInfluencer;
                                return (
                                  <List.Item>
                                    <List.Item.Meta
                                      title={
                                        <Flex justify="space-between">
                                          <Space align="center">
                                            <Button
                                              style={{ padding: 0 }}
                                              type="link"
                                              onClick={() => {
                                                window.open(
                                                  briefInfluencer?.socialMedia
                                                    ?.link,
                                                  "_blank"
                                                );
                                              }}
                                            >
                                              {getPlatformIcon(
                                                briefInfluencer?.socialMedia
                                                  ?.platform || ""
                                              )}
                                              <Avatar
                                                shape="circle"
                                                size="small"
                                                src={
                                                  briefInfluencer?.socialMedia
                                                    ?.profilePicUrl
                                                }
                                              />
                                              <Text strong>
                                                {
                                                  briefInfluencer?.socialMedia
                                                    ?.username
                                                }
                                              </Text>
                                            </Button>
                                          </Space>
                                          <CheckSquareTwoTone twoToneColor="#34C759" />
                                        </Flex>
                                      }
                                    />
                                  </List.Item>
                                );
                              }}
                            />
                          ),
                        },
                      ]}
                    />
                  )}
                </Flex>
              ),
            });
          }
        })
        .catch(async (err) => {
          const responseBody = await err.response.json();
          ToastService.error(responseBody.msg);
          throw err;
        }),
    options: {},
    onSuccess: (c, _, data) => {
      ToastService.success("Influencer suggested successfully");
      c.invalidateQueries({ queryKey: BRIEF_LIST_QUERY_KEY });
      c.invalidateQueries({
        queryKey: [...BRIEF_INFLUENCER_V2_QUERY_KEY, influencerId],
      });
      c.invalidateQueries({
        queryKey: makeBriefInfluencerQueryKey(data?.data?.briefId),
      });
      c.invalidateQueries({
        queryKey: makeBriefSingleInfluencerQueryKey(
          data?.data?.briefId,
          influencerId
        ),
      });
    },
  });
  return {
    suggest,
    isSuggesting,
    isSuggested,
  };
}
